/**
 * `ModuleKontakte` — the Stammdaten "Kontakte" sub-view (default Stammdaten
 * screen). Contact detail for "Petra Sommer" under "Ahornweg 5": breadcrumb +
 * action buttons, a 150px detail sub-nav, a 148px column of spec cards, and a
 * timeline. Ported 1:1 from the prototype `showKontakt` block (Hero.dc.html
 * lines 808–865). Sidebar is the shared `StammdatenSidebar`.
 */
import { ld } from "@/lib/theme/light-dark";
import {
  AtSign,
  Calendar,
  FileText,
  Folder,
  KeyRound,
  LayoutGrid,
  Mail,
  MapPin,
  MessagesSquare,
  Pencil,
  Phone,
  SlidersHorizontal,
  StickyNote,
  User,
  Video,
  Wrench,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { SpecCard } from "../../hero-primitives-app";
import { StammdatenSidebar } from "../../hero-stammdaten-sidebar";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

const CARD_BORDER = ld("#ECEEF1", "#2e3135");

/** Header action button (outlined pill with leading icon). */
function ActionButton(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly dark?: boolean;
}): ReactNode {
  const dark = props.dark === true;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        border: dark ? "none" : `1px solid ${ld("#E7E9ED", "#32363a")}`,
        background: dark ? H.ink : "transparent",
        color: dark ? ld("#fff", "#181b1f") : H.ink,
        borderRadius: 8,
        padding: "7px 11px",
        fontFamily: H.sans,
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {props.icon}
      {props.label}
    </span>
  );
}

/** Detail sub-nav row (left 150px rail). */
function NavRow(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly active?: boolean;
  readonly count?: number;
}): ReactNode {
  const active = props.active === true;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "8px 10px",
        borderRadius: 8,
        fontFamily: H.sans,
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        color: active ? H.accent : H.inkSoft,
        background: active ? H.accentSoft : "transparent",
      }}
    >
      <span
        style={{
          color: active ? H.accent : ld("#6B7280", "#a6aaaf"),
          display: "inline-flex",
        }}
      >
        {props.icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{props.label}</span>
      {props.count !== undefined ? (
        <span style={{ fontSize: 11, color: H.inkGhost }}>{props.count}</span>
      ) : null}
    </div>
  );
}

const RAIL_LABEL: CSSProperties = {
  fontFamily: H.label,
  fontSize: 9.5,
  letterSpacing: "0.1em",
  color: ld("rgba(11,15,25,0.32)", "rgba(250,250,250,0.32)"),
  textTransform: "uppercase",
  padding: "14px 10px 6px",
};

/** Mono header node for the spec cards (leading lucide icon + label). */
function CardTitle(props: {
  readonly icon: ReactNode;
  readonly label: string;
}): ReactNode {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        fontFamily: H.label,
        fontSize: 10,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: H.inkFaint,
      }}
    >
      {props.icon}
      {props.label}
    </span>
  );
}

const EDIT_ACTION: ReactNode = (
  <span
    style={{ color: H.inkGhost, display: "inline-flex", cursor: "pointer" }}
  >
    <Pencil width={13} height={13} strokeWidth={1.8} />
  </span>
);

/** A label/value row inside a spec card (stacked for the narrow column). */
function FieldRow(props: {
  readonly label: string;
  readonly value: ReactNode;
  readonly mono?: boolean;
}): ReactNode {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ color: H.inkFaint, fontSize: 11, lineHeight: 1.2 }}>
        {props.label}
      </span>
      <span
        style={{
          color: H.ink,
          fontWeight: props.mono === true ? 400 : 500,
          fontFamily: props.mono === true ? H.label : H.sans,
          fontSize: props.mono === true ? 11.5 : 12.5,
          lineHeight: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {props.value}
      </span>
    </div>
  );
}

export function ModuleKontakte(props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleKontakte");
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <StammdatenSidebar active="kontakte" setActive={props.setActive} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: "20px 22px",
          overflow: "hidden",
        }}
      >
        {/* Header: breadcrumb + actions */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              minWidth: 0,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  background: ld("#EDEFF2", "#2d3034"),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: H.sans,
                  fontSize: 9,
                  fontWeight: 700,
                  color: ld("#5B6472", "#b2b6bb"),
                }}
              >
                {t("a5")}
              </span>
              <span style={{ fontSize: 13, color: H.inkMute }}>
                {t("ahornweg5")}
              </span>
            </span>
            <span style={{ color: H.inkGhost, fontSize: 15 }}>›</span>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 10 }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: ld("#F6E9EE", "#392e33"),
                  color: ld("#B05B79", "#e68ba9"),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: H.sans,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {t("ps")}
              </span>
              <span
                style={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: 1.2,
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 700, color: H.ink }}>
                  {t("petraSommer")}
                </span>
                <span style={{ fontSize: 12, color: H.inkDim }}>
                  {t("mieterin")}
                </span>
              </span>
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              flexShrink: 0,
            }}
          >
            <ActionButton
              icon={<Calendar width={14} height={14} strokeWidth={1.8} />}
              label={t("meeting")}
            />
            <ActionButton
              icon={<Video width={14} height={14} strokeWidth={1.8} />}
              label={t("video")}
            />
            <ActionButton
              icon={<StickyNote width={14} height={14} strokeWidth={1.8} />}
              label={t("notiz")}
            />
            <ActionButton
              icon={<Mail width={14} height={14} strokeWidth={1.8} />}
              label={t("eMail")}
            />
            <ActionButton
              icon={<Phone width={14} height={14} strokeWidth={1.8} />}
              label={t("anruf")}
              dark
            />
          </div>
        </div>

        {/* Body: 150px rail + 148px cards + timeline */}
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
          {/* Detail sub-nav rail */}
          <div
            style={{
              width: 150,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <NavRow
              icon={<LayoutGrid width={15} height={15} strokeWidth={1.9} />}
              label={t("uebersicht")}
              active
            />
            <NavRow
              icon={<MessagesSquare width={15} height={15} strokeWidth={1.8} />}
              label={t("konversationen")}
            />
            <div style={RAIL_LABEL}>{t("unterlagen")}</div>
            <NavRow
              icon={<Folder width={15} height={15} strokeWidth={1.8} />}
              label={t("dokumente")}
            />
            <div style={RAIL_LABEL}>{t("verknuepfungen")}</div>
            <NavRow
              icon={<FileText width={15} height={15} strokeWidth={1.8} />}
              label={t("vertraege")}
              count={1}
            />
            <NavRow
              icon={<Wrench width={15} height={15} strokeWidth={1.8} />}
              label={t("dienstleister")}
            />
            <NavRow
              icon={<KeyRound width={15} height={15} strokeWidth={1.8} />}
              label={t("portal")}
            />
          </div>

          {/* Spec cards column — narrow so timeline gets more room */}
          <div
            style={{
              width: 148,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <SpecCard
              title={
                <CardTitle
                  icon={<User width={13} height={13} strokeWidth={1.9} />}
                  label={t("stammdaten")}
                />
              }
              action={EDIT_ACTION}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                <FieldRow label={t("anrede")} value="Frau" />
                <FieldRow label={t("vorname")} value="Petra" />
                <FieldRow label={t("nachname")} value="Sommer" />
              </div>
            </SpecCard>

            <SpecCard
              title={
                <CardTitle
                  icon={<MapPin width={13} height={13} strokeWidth={1.9} />}
                  label={t("adresse")}
                />
              }
              action={EDIT_ACTION}
            >
              <span
                style={{
                  fontSize: 11.5,
                  color: ld("rgba(11,15,25,0.42)", "rgba(250,250,250,0.42)"),
                  fontStyle: "italic",
                  lineHeight: 1.35,
                }}
              >
                {t("keineAdresseHinterlegt")}
              </span>
            </SpecCard>

            <SpecCard
              title={
                <CardTitle
                  icon={<AtSign width={13} height={13} strokeWidth={1.9} />}
                  label={t("kontaktdaten")}
                />
              }
              action={EDIT_ACTION}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 6,
                  fontSize: 11.5,
                  color: H.ink,
                  lineHeight: 1.35,
                }}
              >
                <span
                  style={{
                    color: ld("#6B7280", "#a6aaaf"),
                    display: "inline-flex",
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  <Mail width={12} height={12} strokeWidth={1.8} />
                </span>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {t("petraSommerExampleDe")}
                </span>
              </div>
            </SpecCard>

            <SpecCard
              title={
                <CardTitle
                  icon={
                    <SlidersHorizontal
                      width={13}
                      height={13}
                      strokeWidth={1.9}
                    />
                  }
                  label={t("felder")}
                />
              }
              action={EDIT_ACTION}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                <FieldRow label={t("kontaktId")} value="KT-10428" mono />
                <FieldRow
                  label={t("kontaktpraeferenz")}
                  value={<span style={{ color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)") }}>—</span>}
                />
              </div>
            </SpecCard>
          </div>

          {/* Timeline */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 12 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: ld("#EAF0F8", "#2b3037"),
                    color: ld("#4F77AE", "#83ade8"),
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Mail width={15} height={15} strokeWidth={1.9} />
                </span>
                <div
                  style={{
                    flex: 1,
                    width: 1.5,
                    background: ld("#EEF0F3", "#2c2f33"),
                    margin: "5px 0",
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0, paddingBottom: 14 }}>
                <div
                  style={{
                    border: `1px solid ${CARD_BORDER}`,
                    borderRadius: 11,
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: H.ink,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t("rueckfrageNebenkostenabrechnung")}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: H.inkDim,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {t("k14Juni202618")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: ld("#FBF3DC", "#332c18"),
                    color: ld("#9A7B1F", "#c7a752"),
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <StickyNote width={15} height={15} strokeWidth={1.9} />
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    border: `1px solid ${CARD_BORDER}`,
                    borderRadius: 11,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "11px 14px",
                      borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
                    }}
                  >
                    <span
                      style={{ fontSize: 14, fontWeight: 600, color: H.ink }}
                    >
                      {t("notiz")}
                    </span>
                    <span style={{ fontSize: 11, color: H.inkDim }}>
                      {t("k14Juni2026182")}
                    </span>
                  </div>
                  <div
                    style={{
                      padding: "11px 14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ fontSize: 13, color: H.inkSoft }}>
                      {t("mieterinBevorzugtKontaktPer")}
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        color: H.accent,
                        cursor: "pointer",
                      }}
                    >
                      <Pencil width={12} height={12} strokeWidth={1.9} />
                      {t("bearbeiten")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
